import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { NAV_CATEGORIES } from "./PublicNav";
import { trackNav } from "../lib/navTrack";
import { api } from "../lib/api";

// Terminal command palette — visitors press "/" to jump to any public page.

const ACCENT = {
    learn: "#22d3ee", practice: "#4ade80", compete: "#a855f7",
    hunt: "#fbbf24", community: "#38bdf8", company: "#f43f5e",
};

const VISITOR_PAGES = NAV_CATEGORIES.flatMap((cat) =>
    (cat.links || cat.groups.flatMap(([, ls]) => ls)).map(([name, path, desc]) => ({
        name, path, desc: desc || "", category: cat.key,
    }))
);

// Logged-in members: mirrors the left sidebar destinations.
const MEMBER_PAGES = [
    ["Home Feed", "/", "your personalised feed", "community"],
    ["Academy", "/learn", "guided hands-on modules", "learn"],
    ["Career Tracks", "/tracks", "structured learning paths", "learn"],
    ["Certifications", "/certifications", "practical certs", "learn"],
    ["Vulnerable Labs", "/labs", "real-world targets", "practice"],
    ["Attack Box", "/box", "kali in your browser", "practice"],
    ["Network Labs", "/network-labs", "multi-host networks", "practice"],
    ["Investigations", "/investigations", "forensics scenarios", "practice"],
    ["Payload Tools", "/tools", "encoders & generators", "practice"],
    ["CTF Arena", "/ctf", "capture the flag", "compete"],
    ["Tournaments", "/tournaments", "scheduled championships", "compete"],
    ["Events", "/events", "conferences & hackathons", "compete"],
    ["Multiplayer", "/multiplayer", "head-to-head rooms", "compete"],
    ["Leaderboard", "/leaderboard", "global rankings", "compete"],
    ["Quests & XP", "/quests", "daily challenges", "compete"],
    ["Teams", "/teams", "hacking squads", "compete"],
    ["Bug Bounty Programs", "/programs", "public program directory", "hunt"],
    ["Security Jobs", "/jobs", "infosec career board", "hunt"],
    ["Communities", "/communities", "subforums & hubs", "community"],
    ["Q&A", "/q", "peer q&a and writeups", "community"],
    ["News", "/news", "cves & advisories", "community"],
    ["Chat", "/chat", "encrypted member chat", "community"],
    ["Inbox", "/inbox", "notifications & threads", "community"],
    ["Bookmarks", "/bookmarks", "your saved posts", "company"],
    ["Billing & Plans", "/billing", "membership & box hours", "company"],
    ["Settings", "/settings", "account preferences", "company"],
].map(([name, path, desc, category]) => ({ name, path, desc, category }));

const RECENT_KEY = "hc_palette_recent";

const VISITOR_ACTIONS = [
    ["Start Attack Box", "/box", "boot kali in your browser", "practice"],
    ["Ask a question", "/ask", "post to the community q&a", "community"],
    ["Join free", "/login", "create your hacker profile", "company"],
].map(([name, path, desc, category]) => ({ name, path, desc, category, action: true }));

const MEMBER_ACTIONS = [
    ["Ask a question", "/ask", "post to the community q&a", "community"],
    ["Start Attack Box", "/box", "boot kali in your browser", "practice"],
    ["Claim daily quests", "/quests", "stack xp, keep the streak", "compete"],
    ["Open chat", "/chat", "encrypted member chat", "community"],
].map(([name, path, desc, category]) => ({ name, path, desc, category, action: true }));

function loadRecents(pages) {
    try {
        const raw = JSON.parse(localStorage.getItem(RECENT_KEY) || "[]");
        return raw.map((r) => pages.find((p) => p.path === r)).filter(Boolean).slice(0, 5);
    } catch { return []; }
}

function saveRecent(path) {
    try {
        const raw = JSON.parse(localStorage.getItem(RECENT_KEY) || "[]").filter((p) => p !== path);
        localStorage.setItem(RECENT_KEY, JSON.stringify([path, ...raw].slice(0, 5)));
    } catch { /* private mode */ }
}

function isTypingTarget(el) {
    if (!el) return false;
    const tag = el.tagName;
    return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || el.isContentEditable;
}

export default function CommandPalette({ member = false }) {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    const [sel, setSel] = useState(0);
    const inputRef = useRef(null);
    const navigate = useNavigate();
    const pages = member ? MEMBER_PAGES : VISITOR_PAGES;
    const actions = member ? MEMBER_ACTIONS : VISITOR_ACTIONS;
    const [flagState, setFlagState] = useState(null);
    const isFlag = member && /^hc\{.+\}$/i.test(query.trim());

    const submitFlag = useCallback(async () => {
        setFlagState({ busy: true });
        try {
            const { data } = await api.post("/labs/submit-flag", { flag: query.trim() });
            setFlagState({
                correct: !!data.correct,
                message: data.correct
                    ? `✓ ${data.lab_title || "challenge"} ${data.kind === "ctf" ? "solved" : "rooted"}${data.xp_awarded ? ` · +${data.xp_awarded} ${data.kind === "ctf" ? "karma" : "XP"}` : ""}${data.first_blood ? " · 🩸 first blood" : ""}`
                    : `✗ ${data.message || "wrong flag"}`,
            });
        } catch (e) {
            setFlagState({ correct: false, message: `✗ ${e?.response?.data?.detail?.message || e?.response?.data?.detail || "submission failed"}` });
        }
    }, [query]);

    useEffect(() => {
        const onKey = (e) => {
            if (e.key === "/" && !e.metaKey && !e.ctrlKey && !e.altKey && !isTypingTarget(e.target)) {
                e.preventDefault();
                setOpen(true);
            }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, []);

    useEffect(() => {
        if (!open) { setQuery(""); setSel(0); return undefined; }
        inputRef.current?.focus();
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => { document.body.style.overflow = prev; };
    }, [open]);

    const results = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) {
            const recents = loadRecents(pages).map((p) => ({ ...p, recent: true }));
            const rest = pages.filter((p) => !recents.some((r) => r.path === p.path));
            return [...actions, ...recents, ...rest];
        }
        const match = (p) =>
            p.name.toLowerCase().includes(q) || p.path.includes(q) ||
            p.category.includes(q) || p.desc.toLowerCase().includes(q);
        return [...actions.filter(match), ...pages.filter(match)];
    }, [query, pages, actions]);

    const go = useCallback((page) => {
        if (!page.action) saveRecent(page.path);
        trackNav("palette", page.category, page.path, member ? "member" : "visitor");
        setOpen(false);
        navigate(page.path);
    }, [navigate, member]);

    const onInputKey = (e) => {
        if (e.key === "Escape") { setOpen(false); return; }
        if (isFlag && e.key === "Enter") { e.preventDefault(); submitFlag(); return; }
        if (e.key === "ArrowDown") { e.preventDefault(); setSel((s) => Math.min(s + 1, results.length - 1)); }
        if (e.key === "ArrowUp") { e.preventDefault(); setSel((s) => Math.max(s - 1, 0)); }
        if (e.key === "Enter" && results[sel]) { e.preventDefault(); go(results[sel]); }
    };

    if (!open) return null;
    return createPortal(
        <div className="fixed inset-0 z-[90]" role="dialog" aria-modal="true" aria-label="Command palette" data-testid="command-palette">
            <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setOpen(false)} aria-hidden="true" />
            <div className="relative mx-auto mt-[10vh] w-[min(620px,calc(100vw-1.5rem))] bg-[#0a0f1a]/[0.98] border border-cyan-500/25 rounded-md shadow-2xl shadow-black/80 overflow-hidden
                motion-safe:animate-in motion-safe:fade-in-0 motion-safe:slide-in-from-top-2 motion-safe:duration-150"
                 style={{ boxShadow: "0 24px 60px rgba(0,0,0,0.8), 0 0 28px rgba(34,211,238,0.12)" }}>
                <div className="flex items-center justify-between px-4 pt-3 pb-1 font-mono text-[9px] uppercase tracking-[0.3em] text-slate-600">
                    <span>~/hacking.community — jump to page</span>
                    <span>esc to close</span>
                </div>
                <div className="flex items-center gap-2 px-4 py-2 border-b border-white/[0.07]">
                    <span className="font-mono text-cyan-300 font-bold" aria-hidden="true">&gt;_</span>
                    <input ref={inputRef} value={query}
                           onChange={(e) => { setQuery(e.target.value); setSel(0); }}
                           onKeyDown={onInputKey}
                           placeholder="type a page name…"
                           aria-label="Search pages"
                           data-testid="command-palette-input"
                           className="flex-1 bg-transparent outline-none font-mono text-sm text-slate-100 placeholder:text-slate-600 caret-cyan-300 h-9" />
                </div>
                <ul className="max-h-[46vh] overflow-y-auto p-1.5" data-testid="command-palette-results">
                    {isFlag && (
                        <li>
                            <button onClick={submitFlag} disabled={flagState?.busy} data-testid="palette-submit-flag"
                                    className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-sm text-left bg-emerald-500/10 border border-emerald-500/30 hover:bg-emerald-500/15 transition-colors">
                                <span className="font-mono text-[12px] font-black text-emerald-300" aria-hidden="true">⚑</span>
                                <span className="text-[13px] text-emerald-200 font-mono">{flagState?.busy ? "submitting…" : "submit this flag"}</span>
                                <span className="ml-auto font-mono text-[10px] text-slate-500">↵ enter</span>
                            </button>
                            {flagState?.message && (
                                <div data-testid="palette-flag-result"
                                     className={`px-3 py-2 mt-1 font-mono text-[12px] rounded-sm ${flagState.correct ? "text-emerald-300 bg-emerald-500/10" : "text-rose-300 bg-rose-500/10"}`}>
                                    {flagState.message}
                                </div>
                            )}
                        </li>
                    )}
                    {!isFlag && results.length === 0 && (
                        <li className="px-4 py-6 font-mono text-xs text-slate-500">// no match — try &quot;labs&quot;, &quot;ctf&quot;, &quot;jobs&quot;…</li>
                    )}
                    {!isFlag && results.map((p, i) => (
                        <li key={`${p.action ? "a" : "p"}-${p.path}`}>
                            {i === 0 && p.action && (
                                <div className="px-3 pt-1 pb-0.5 font-mono text-[9px] uppercase tracking-widest text-amber-400/70">// quick actions</div>
                            )}
                            {i === 0 && !p.action && p.recent && (
                                <div className="px-3 pt-1 pb-0.5 font-mono text-[9px] uppercase tracking-widest text-slate-600">// recent jumps</div>
                            )}
                            {!p.action && p.recent && i > 0 && results[i - 1].action && (
                                <div className="px-3 pt-2 pb-0.5 font-mono text-[9px] uppercase tracking-widest text-slate-600 border-t border-white/[0.05] mt-1">// recent jumps</div>
                            )}
                            {!p.action && !p.recent && i > 0 && (results[i - 1].recent || results[i - 1].action) && (
                                <div className="px-3 pt-2 pb-0.5 font-mono text-[9px] uppercase tracking-widest text-slate-600 border-t border-white/[0.05] mt-1">// all pages</div>
                            )}
                            <button onClick={() => go(p)} onMouseEnter={() => setSel(i)}
                                    data-testid={`palette-item${p.path.replace(/\//g, "-")}`}
                                    className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-sm text-left transition-colors motion-reduce:transition-none
                                        ${i === sel ? "bg-white/[0.06]" : ""}`}>
                                {p.action ? (
                                    <span className="font-mono text-[12px] font-black text-amber-300 shrink-0 w-1.5" aria-hidden="true">$</span>
                                ) : (
                                    <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: ACCENT[p.category] || "#64748b" }} aria-hidden="true" />
                                )}
                                <span className="text-[13px] text-slate-200 whitespace-nowrap">{p.name}</span>
                                <span className="font-mono text-[10px] text-slate-500 truncate">{p.desc}</span>
                                <span className="ml-auto font-mono text-[10px] text-slate-600 shrink-0">{p.path}</span>
                            </button>
                        </li>
                    ))}
                </ul>
                <div className="px-4 py-2 border-t border-white/[0.07] font-mono text-[9.5px] text-slate-600 flex gap-4">
                    <span>↑↓ navigate</span><span>↵ open</span><span>/ anywhere reopens</span>
                    {member && <span className="text-emerald-500/70">paste hc{"{...}"} to submit a flag</span>}
                </div>
            </div>
        </div>,
        document.body
    );
}
